import type { PropsWithChildren } from "react";
import { Navigate, Route, Routes } from "react-router";
import type { AccessPermission } from "@entrilia/shared/api/types";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import ClientManagementPage from "./components/pages/client-management/ClientManagementPage";
import EntriliaUsersManagementPage from "./components/pages/entrilia-users-management/EntriliaUsersManagementPage";
import CopilotAgentsPage from "./components/pages/copilot-agents/CopilotAgentsPage";
import ObserverIssuesPage from "./components/pages/observer/ObserverIssuesPage";
import SystemMaintenancePage from "./components/pages/system-maintenance/SystemMaintenancePage";
import UserContext from "./context/UserContext";
import { defaultOpsRoute, pageRoutes, routePermissions } from "./routes";

interface PermissionGuardProps {
  permissions: AccessPermission[];
  fallbackRoute: string;
}

const PermissionGuard = ({
  permissions: required,
  fallbackRoute,
  children,
}: PropsWithChildren<PermissionGuardProps>) => {
  const { permissions } = useDefinedContext(UserContext);
  const hasAccess = required.some((p) => permissions.includes(p));
  if (!hasAccess) {
    return <Navigate replace to={`/${fallbackRoute}`} />;
  }
  return <>{children}</>;
};

const Pages = () => {
  const { permissions } = useDefinedContext(UserContext);

  const defaultRoute = defaultOpsRoute(permissions);

  return (
    <Routes>
      <Route path="/" element={<Navigate replace to={`/${defaultRoute}`} />} />
      <Route
        path={pageRoutes.systemMaintenance}
        element={
          <PermissionGuard permissions={routePermissions[pageRoutes.systemMaintenance]} fallbackRoute={defaultRoute}>
            <SystemMaintenancePage />
          </PermissionGuard>
        }
      />
      <Route
        path={pageRoutes.clientManagement}
        element={
          <PermissionGuard permissions={routePermissions[pageRoutes.clientManagement]} fallbackRoute={defaultRoute}>
            <ClientManagementPage />
          </PermissionGuard>
        }
      />
      <Route
        path={pageRoutes.entriliaUsersManagement}
        element={
          <PermissionGuard
            permissions={routePermissions[pageRoutes.entriliaUsersManagement]}
            fallbackRoute={defaultRoute}
          >
            <EntriliaUsersManagementPage />
          </PermissionGuard>
        }
      />
      <Route
        path={pageRoutes.agents}
        element={
          <PermissionGuard permissions={routePermissions[pageRoutes.agents]} fallbackRoute={defaultRoute}>
            <CopilotAgentsPage />
          </PermissionGuard>
        }
      />
      <Route
        path={pageRoutes.observer}
        element={
          <PermissionGuard permissions={routePermissions[pageRoutes.observer]} fallbackRoute={defaultRoute}>
            <ObserverIssuesPage />
          </PermissionGuard>
        }
      />
      <Route
        path={`${pageRoutes.observer}/runs/:runId`}
        element={
          <PermissionGuard permissions={routePermissions[pageRoutes.observer]} fallbackRoute={defaultRoute}>
            <ObserverIssuesPage />
          </PermissionGuard>
        }
      />
      <Route path="*" element={<Navigate replace to={`/${defaultRoute}`} />} />
    </Routes>
  );
};

export default Pages;
